/**
 * Llamadas HTTP del dominio del Cotizador de puertas (cotizador de USO).
 *
 * SIEMPRE sobre el cliente único `lib/api` (`docs/frontend_architecture.md`, Pr. 2):
 * ningún `fetch`/`ky` suelto. El cliente ya aporta `credentials: 'include'`, el
 * `X-CSRF-TOKEN` y el `X-Sede-Id` (sede activa) en cada request.
 *
 * El **motor de cálculo** (`cotizador_*.py` / `BACK CON CEREBRO`) es zona prohibida; estas
 * llamadas solo consumen la API REST que ya lo envuelve (`CotizadorService`). Réplica de la
 * superficie de `application/api/domains/cotizador.py` que usa la pantalla de USO:
 *
 *   POST /api/v1/cotizador/calcular            -> total + ticket + imágenes (use_cotizador)
 *   POST /api/v1/cotizador/ticket_produccion   -> ticket de producción (use_cotizador)
 *   POST /api/v1/cotizador/ticket_cliente      -> ticket de cliente (use_cotizador)
 *   GET  /api/v1/cotizador/cotizaciones        -> historial (use_cotizador + admin)
 *   POST /api/v1/cotizador/cotizaciones        -> guardar en historial (use_cotizador)
 *   GET  /api/v1/cotizador/cotizaciones/stats  -> stats de error (use_cotizador + admin)
 *
 * La PANTALLA de precios del cotizador ya existe (`features/admin` PreciosScreen, #58): NO
 * se duplica aquí. Un `ApiError` (400 por medidas inválidas, 401/403, 5xx) lo propaga el
 * cliente y lo gestionan los hooks/pantalla.
 */
import { api } from "@/lib/api";

import type {
  CalcResult,
  CotizacionHistoryCreated,
  CotizacionHistoryList,
  CotizacionStats,
  CotizadorInput,
  TicketResult,
} from "./types";

/**
 * Calcula la(s) puerta(s) (`POST /cotizador/calcular`). El cuerpo va en JSON. El backend
 * valida las medidas (400 si son inválidas) e invoca el motor standalone; devuelve total,
 * ticket de texto (filtrado por rol) e imágenes. Devuelve el `CalcResult`.
 */
export async function calcularPuerta(input: CotizadorInput): Promise<CalcResult> {
  const response = await api.post("cotizador/calcular", { json: input });
  return (await response.json()) as CalcResult;
}

/**
 * Genera el ticket de producción (`POST /cotizador/ticket_produccion`). El cuerpo es el
 * mismo payload del cálculo (el backend reusa/recalcula). Para no-admin oculta los refuerzos.
 */
export async function ticketProduccion(input: CotizadorInput): Promise<TicketResult> {
  const response = await api.post("cotizador/ticket_produccion", { json: input });
  return (await response.json()) as TicketResult;
}

/**
 * Genera el ticket de cliente (`POST /cotizador/ticket_cliente`). El cuerpo es el payload
 * del cálculo. Para no-admin oculta los refuerzos.
 */
export async function ticketCliente(input: CotizadorInput): Promise<TicketResult> {
  const response = await api.post("cotizador/ticket_cliente", { json: input });
  return (await response.json()) as TicketResult;
}

/** Cuerpo de guardado de una cotización en el historial (cálculo + comparación opcional). */
export interface SaveCotizacionBody extends CotizadorInput {
  total_cotizador: number;
  // El cálculo puede no traer estos campos (no-admin no recibe peso); se aceptan ausentes.
  m2_total?: number | undefined;
  peso_estimado_kg?: number | undefined;
}

/**
 * Guarda una cotización en el historial (`POST /cotizador/cotizaciones`). El backend valida
 * medidas y total (400 si inválidos) y la persiste con el usuario y la sede activa. Devuelve
 * la cotización creada (201). El historial es solo admin para leer, pero crear es de cualquier
 * `use_cotizador` (igual que el SSR, que guardaba al pulsar COTIZAR).
 */
export async function guardarCotizacion(
  body: SaveCotizacionBody,
): Promise<CotizacionHistoryCreated> {
  const response = await api.post("cotizador/cotizaciones", { json: body });
  return (await response.json()) as CotizacionHistoryCreated;
}

/**
 * Historial de cotizaciones de la sede activa (`GET /cotizador/cotizaciones`). Solo admin
 * (el backend devuelve 403 si no). `limit` por defecto 50 (el backend lo acota a 200);
 * `mine=0` trae todas las del admin, `mine=1` solo las propias.
 */
export async function fetchCotizaciones(
  limit = 50,
  mine = false,
): Promise<CotizacionHistoryList> {
  const response = await api.get("cotizador/cotizaciones", {
    searchParams: { limit, mine: mine ? "1" : "0" },
  });
  return (await response.json()) as CotizacionHistoryList;
}

/**
 * Estadísticas de error (%) cotizador vs vendedor (`GET /cotizador/cotizaciones/stats`).
 * Solo admin (403 si no). `days` por defecto 30 (acotado a 365 por el backend).
 */
export async function fetchCotizacionStats(
  days = 30,
  mine = false,
): Promise<CotizacionStats> {
  const response = await api.get("cotizador/cotizaciones/stats", {
    searchParams: { days, mine: mine ? "1" : "0" },
  });
  return (await response.json()) as CotizacionStats;
}
